import { DataTypeProvider } from '@devexpress/dx-react-grid';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { useParams } from 'react-router-dom';
import NRSecondaryLink from '../../NRSecondaryLink/NRSecondaryLink';
import CorrelationsGrid from './CorrelationsGrid/CorrelationsGrid';
import NRCorrelationsGridContainer from './NRCorrelationsGrid/NRCorrelationsGridContainer';
import { numberFilterOperations } from '../../NRReactGrid/NRReactGrid.utils';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import NRDialog from '../../NRDialog/NRDialog';

const EnhancedCorrelationsFormatter = ({ value, column, row, assetCorrelationType, composedAssetId, getListAssetCorrelationsIdentifier }) => {
  const theme = useTheme();
  const { componentId: routeComposedAssetId } = useParams();
  const resolvedComposedAssetId = composedAssetId ?? routeComposedAssetId;
  const valueToUse = Array.isArray(value) ? value.length : value;
  const [open, setOpen] = useState(false);

  const correlationListIdentifier = typeof getListAssetCorrelationsIdentifier === 'function' ? getListAssetCorrelationsIdentifier(row) : undefined;
  const useListAssetCorrelationsQuery = !!assetCorrelationType && !!correlationListIdentifier;

  return (
    <>
      <Tooltip title={valueToUse > 0 ? column.tooltipA + valueToUse + column.tooltipB : ''}>
        <NRSecondaryLink
          value={valueToUse}
          handleOnClick={() => setOpen(true)}
          openNull={false}
          aria-label={`Correlations: ${valueToUse}. Opens correlation report.`}
        />
      </Tooltip>
      <NRDialog isOpen={open} setIsOpen={setOpen} fullWidth={true} maxWidth="md">
        <DialogTitle
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            py: theme.spacing(1.5)
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h3" component="span">
              {`${row?.title || 'Correlations'} - ${valueToUse}`}
            </Typography>
            <IconButton aria-label="Close" onClick={() => setOpen(false)} size="large">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '70vh', p: theme.spacing(3, 3, 0) }}>
          {open &&
            (useListAssetCorrelationsQuery ? (
              <NRCorrelationsGridContainer
                setOpen={setOpen}
                composedAssetId={resolvedComposedAssetId}
                correlationType={assetCorrelationType}
                identifier={correlationListIdentifier}
              />
            ) : (
              <CorrelationsGrid setOpen={setOpen} rows={row?.correlations} />
            ))}
        </DialogContent>
        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: theme.spacing(3),
            py: theme.spacing(2)
          }}
        >
          <Button variant="contained" color="secondary" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </NRDialog>
    </>
  );
};

export const EnhancedCorrelationsProvider = ({ extraParams, ...props }) => {
  const extraParamsRef = useRef(extraParams ?? {});
  extraParamsRef.current = extraParams ?? {};

  const Formatter = useCallback(formatterProps => <EnhancedCorrelationsFormatter {...formatterProps} {...extraParamsRef.current} />, []);

  return <DataTypeProvider formatterComponent={Formatter} availableFilterOperations={numberFilterOperations} {...props} />;
};
